import React from 'react';
import { Pencil, Loader2, ChevronRight, Eye, XCircle } from 'lucide-react';
import { ProfileWithHorseCount } from '../types/api';

const STATUS_FOLDER_CONFIG: Array<{
  value: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}> = [
  { value: 'watch', label: 'Watching', icon: Eye },
  { value: 'own', label: 'Own', icon: Eye },
  { value: 'sold', label: 'Sold', icon: Eye },
  { value: 'skip', label: 'Skipped', icon: XCircle },
];

interface ProfileListViewProps {
  profiles: ProfileWithHorseCount[];
  loading: boolean;
  totalHorseCount: number;
  statusCounts: Record<string, number>;
  onSelectProfile: (profileId: string | null) => void;
  onSelectStatus: (status: string) => void;
  onEditProfile: (profile: ProfileWithHorseCount) => void;
  onCreateProfile: () => void;
  onSearchMyHorses: () => void;
}

export const ProfileListView: React.FC<ProfileListViewProps> = ({
  profiles,
  loading,
  totalHorseCount,
  statusCounts,
  onSelectProfile,
  onSelectStatus,
  onEditProfile,
  onCreateProfile,
  onSearchMyHorses,
}) => {
  const visibleStatuses = STATUS_FOLDER_CONFIG.filter(s => (statusCounts[s.value] ?? 0) > 0);

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Search My Horses */}
      <div className="flex justify-center w-full mb-2">
        <button
          onClick={onSearchMyHorses}
          className="inline-flex items-center justify-center gap-3 w-80 px-8 py-5 text-base font-semibold text-[#132B64] bg-white border-2 border-[#132B64] rounded-full hover:bg-gray-50 transition-colors"
        >
          <img src="/icons/search_icon.svg" alt="" className="h-6 w-6" />
          Search My Horses
        </button>
      </div>

      {/* Profile list */}
      <div className="w-full max-w-[320px] lg:max-w-[500px] flex flex-col gap-2">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* All Horses row */}
            <button
              onClick={() => onSelectProfile(null)}
              className="w-full flex items-center justify-between bg-[#E7EEE3] rounded-2xl px-5 py-4 hover:brightness-95 transition-all text-left"
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-gray-900">All Horses</span>
                <span className="text-xs text-gray-500 mt-0.5">Your entire collection</span>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <span className="text-sm font-bold text-[#132B64]">{totalHorseCount}</span>
                <ChevronRight size={16} className="text-gray-400" />
              </div>
            </button>

            {/* Profile rows */}
            {profiles.map(profile => (
              <div
                key={profile.id}
                className="w-full flex items-center bg-[#E7EEE3] rounded-2xl px-5 py-4"
              >
                <button
                  onClick={() => onSelectProfile(profile.id)}
                  className="flex-1 flex items-center gap-3 text-left min-w-0"
                >
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-sm font-semibold text-gray-900 truncate">
                      {profile.name}
                    </span>
                    {profile.description && (
                      <span className="text-xs text-gray-500 mt-0.5 truncate">
                        {profile.description}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-sm font-bold text-[#132B64]">
                      {profile.horse_count}
                    </span>
                    <ChevronRight size={16} className="text-gray-400" />
                  </div>
                </button>
                <button
                  onClick={e => { e.stopPropagation(); onEditProfile(profile); }}
                  className="ml-2 h-8 w-8 flex items-center justify-center rounded-full hover:bg-white/60 text-gray-400 hover:text-gray-700 transition-colors shrink-0"
                  aria-label={`Edit ${profile.name}`}
                >
                  <Pencil size={14} />
                </button>
              </div>
            ))}

            {/* Add Profile button */}
            <button
              onClick={onCreateProfile}
              className="w-full flex items-center justify-center gap-2 mt-1 py-3 text-sm font-semibold text-[#132B64] border-2 border-dashed border-[#132B64]/30 rounded-2xl hover:bg-[#132B64]/5 transition-colors"
            >
              <span className="text-lg leading-none">+</span>
              Add Profile
            </button>

            {/* Status folders */}
            {visibleStatuses.length > 0 && (
              <>
                <div className="mt-3 mb-1 px-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">By Status</span>
                </div>
                {visibleStatuses.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => onSelectStatus(value)}
                    className="w-full flex items-center justify-between bg-[#E7EEE3] rounded-2xl px-5 py-4 hover:brightness-95 transition-all text-left"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon size={16} className="text-gray-500 shrink-0" />
                      <span className="text-sm font-semibold text-gray-900">{label}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <span className="text-sm font-bold text-[#132B64]">{statusCounts[value]}</span>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                  </button>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
